import { useEffect, useMemo, useRef, useState } from 'react';

import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
} from '@/components/ui';
import { useUsers, useDepartments } from '@/lib/admin-store';
import { useWorkflows } from '@/lib/workflows-store';
import {
  useActivityTypeActions,
  useActivityTypeVisibility,
  type ActivityType,
  type VisibilityScope,
  type VisibilityScopeType,
} from '@/lib/activity-types-store';

/**
 * `<VisibilityDialog>` — Modal admin pra configurar quem enxerga um
 * activity_type (U.2 / FEAT-V2-013).
 *
 * 4 alcances coexistem (somam-se):
 *   Todo mundo — visível pra todos (scope_id = NULL)
 *   Fluxo de trabalho — visível em fluxos específicos (scope_id = workflow.id)
 *   Departamento — visível em depts específicos (scope_id = dept.slug)
 *   Pessoa — visível pra users específicos (scope_id = user.id)
 *
 * Validação cliente: array `scopes.length >= 1` antes do submit (backend
 * exige mesmo via Zod `.min(1)`).
 */

interface VisibilityDialogProps {
  open: boolean;
  onClose: () => void;
  activityType: ActivityType;
}

export function VisibilityDialog({ open, onClose, activityType }: VisibilityDialogProps) {
  const { data: vis, isLoading } = useActivityTypeVisibility(open ? activityType.id : null);
  const actions = useActivityTypeActions();
  const { data: workflows = [] } = useWorkflows({ ativo: true });
  const departments = useDepartments();
  const users = useUsers();

  // Estado local dos scopes (carrega do backend ao abrir)
  const [scopes, setScopes] = useState<VisibilityScope[]>([]);
  const [userSearch, setUserSearch] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Carrega scopes do backend APENAS na primeira vez que vis fica disponível
  // (não a cada re-fetch — evita sobrescrever mudanças locais).
  const hasInitializedRef = useRef(false);
  useEffect(() => {
    if (open && vis && !hasInitializedRef.current) {
      setScopes(vis.scopes ?? []);
      hasInitializedRef.current = true;
    }
    if (!open) {
      hasInitializedRef.current = false;
      setScopes([]);
      setUserSearch('');
      setError(null);
    }
  }, [open, vis]);

  // Helpers de toggle
  function hasScope(type: VisibilityScopeType, id: string | null = null): boolean {
    return scopes.some((s) => s.scopeType === type && (s.scopeId ?? null) === id);
  }

  function toggleScope(type: VisibilityScopeType, id: string | null = null) {
    setError(null);
    setScopes((prev) => {
      const exists = prev.some((s) => s.scopeType === type && (s.scopeId ?? null) === id);
      if (exists) {
        return prev.filter((s) => !(s.scopeType === type && (s.scopeId ?? null) === id));
      }
      return [...prev, { scopeType: type, scopeId: id }];
    });
  }

  const filteredUsers = useMemo(() => {
    if (!userSearch.trim()) return users.filter((u) => u.ativo);
    const q = userSearch.trim().toLowerCase();
    return users.filter(
      (u) => u.ativo && (u.nome.toLowerCase().includes(q) || u.email.toLowerCase().includes(q))
    );
  }, [users, userSearch]);

  // Count por escopo (pra badges nas secções)
  const countByScope = useMemo(() => {
    return {
      global: scopes.filter((s) => s.scopeType === 'global').length,
      workflow: scopes.filter((s) => s.scopeType === 'workflow').length,
      dept: scopes.filter((s) => s.scopeType === 'dept').length,
      user: scopes.filter((s) => s.scopeType === 'user').length,
    };
  }, [scopes]);

  async function handleSave() {
    if (scopes.length === 0) {
      setError('Marque pelo menos uma opção de quem pode ver.');
      return;
    }
    setError(null);
    try {
      await actions.updateVisibility(activityType.id, scopes);
      onClose();
    } catch (e) {
      setError(
        e instanceof Error
          ? `Não foi possível salvar. Detalhe: ${e.message}`
          : 'Não foi possível salvar. Tente de novo.',
      );
    }
  }

  const saveDisabled = scopes.length === 0 || isLoading || actions.pending !== null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()} size="md">
      <DialogHeader>
        <DialogTitle>
          Quem pode ver — {activityType.label}
        </DialogTitle>
      </DialogHeader>
      <DialogContent>
        <div className="min-w-0 space-y-4" data-testid="visibility-dialog">
          {error && <Alert variant="danger" rail>{error}</Alert>}

          <Alert variant="info" rail>
            Escolha quem enxerga este tipo de atividade. As opções se somam: quem se encaixar em
            qualquer uma delas passa a ver. Marque pelo menos uma.
          </Alert>

          {isLoading && (
            <p className="text-sm italic text-ink-3 dark:text-ink-dark-3">
              Carregando as opções…
            </p>
          )}

          {/* Todo mundo */}
          <Card className="min-w-0">
            <div className="min-w-0 p-3">
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <div className="text-sm font-medium text-ink-1 dark:text-ink-dark-1">
                  Todo mundo
                </div>
                {countByScope.global > 0 && (
                  <Badge variant="success" size="sm">Ligado</Badge>
                )}
              </div>
              <Checkbox
                label="Visível pra todo mundo"
                checked={hasScope('global', null)}
                onChange={() => toggleScope('global', null)}
                data-testid="visibility-global"
              />
            </div>
          </Card>

          {/* Fluxo de trabalho */}
          <Card className="min-w-0">
            <div className="min-w-0 p-3">
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <div className="text-sm font-medium text-ink-1 dark:text-ink-dark-1">
                  Por fluxo de trabalho
                </div>
                {countByScope.workflow > 0 && (
                  <Badge variant="info" size="sm">{countByScope.workflow} marcados</Badge>
                )}
              </div>
              <div className="max-h-40 space-y-1.5 overflow-y-auto">
                {workflows.length === 0 ? (
                  <p className="text-xs italic text-ink-3 dark:text-ink-dark-3">
                    Nenhum fluxo de trabalho em uso.
                  </p>
                ) : (
                  workflows.map((w) => (
                    <Checkbox
                      key={w.id}
                      className="w-full"
                      label={
                        <span className="min-w-0">
                          <span className="text-ink-2 dark:text-ink-dark-2">{w.deptSlug}/</span>
                          {w.nome}
                        </span>
                      }
                      checked={hasScope('workflow', w.id)}
                      onChange={() => toggleScope('workflow', w.id)}
                      data-testid={`visibility-workflow-${w.id}`}
                    />
                  ))
                )}
              </div>
            </div>
          </Card>

          {/* Departamento */}
          <Card className="min-w-0">
            <div className="min-w-0 p-3">
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <div className="text-sm font-medium text-ink-1 dark:text-ink-dark-1">
                  Por departamento
                </div>
                {countByScope.dept > 0 && (
                  <Badge variant="info" size="sm">{countByScope.dept} marcados</Badge>
                )}
              </div>
              <div className="max-h-40 space-y-1.5 overflow-y-auto">
                {departments.length === 0 ? (
                  <p className="text-xs italic text-ink-3 dark:text-ink-dark-3">
                    Nenhum departamento cadastrado.
                  </p>
                ) : (
                  departments.map((d) => (
                    <Checkbox
                      key={d.slug}
                      className="w-full"
                      label={d.nome}
                      checked={hasScope('dept', d.slug)}
                      onChange={() => toggleScope('dept', d.slug)}
                      data-testid={`visibility-dept-${d.slug}`}
                    />
                  ))
                )}
              </div>
            </div>
          </Card>

          {/* Pessoa */}
          <Card className="min-w-0">
            <div className="min-w-0 p-3">
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <div className="text-sm font-medium text-ink-1 dark:text-ink-dark-1">
                  Por pessoa
                </div>
                {countByScope.user > 0 && (
                  <Badge variant="info" size="sm">{countByScope.user} marcadas</Badge>
                )}
              </div>
              <Input
                type="search"
                placeholder="Buscar pessoa…"
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                aria-label="Buscar pessoa"
                data-testid="visibility-user-search"
                className="mb-2"
              />
              <div className="max-h-40 space-y-1.5 overflow-y-auto">
                {filteredUsers.length === 0 ? (
                  <p className="text-xs italic text-ink-3 dark:text-ink-dark-3">
                    {userSearch ? 'Nenhuma pessoa encontrada.' : 'Nenhuma pessoa ativa.'}
                  </p>
                ) : (
                  filteredUsers.map((u) => (
                    <Checkbox
                      key={u.id}
                      className="w-full"
                      label={
                        <span className="min-w-0 break-words">
                          {u.nome}{' '}
                          <span className="text-xs text-ink-3 dark:text-ink-dark-3">
                            ({u.email})
                          </span>
                        </span>
                      }
                      checked={hasScope('user', u.id)}
                      onChange={() => toggleScope('user', u.id)}
                      data-testid={`visibility-user-${u.id}`}
                    />
                  ))
                )}
              </div>
            </div>
          </Card>

          {scopes.length === 0 && (
            <p className="text-center text-xs italic text-ink-3 dark:text-ink-dark-3">
              Marque pelo menos uma opção pra poder salvar.
            </p>
          )}
        </div>
      </DialogContent>
      <DialogFooter>
        <Button type="button" variant="ghost" onClick={onClose}>Cancelar</Button>
        <Button
          type="button"
          onClick={handleSave}
          disabled={saveDisabled}
          loading={actions.pending === `visibility-${activityType.id}`}
          data-testid="visibility-save"
        >
          Salvar
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

// ─── Helper: badge resumo da visibilidade (pra card de tipo) ────────────

/**
 * Renderiza badge resumindo quem enxerga o tipo:
 *   - Só todo mundo → "Global" verde
 *   - Só alcances restritos → "Restrito: N" amarelo
 *   - Todo mundo + restrições → "Global + N" azul
 *   - Sem nada configurado (legacy/migração pendente) → null (não renderiza)
 */
export function VisibilityBadge({ scopes }: { scopes: VisibilityScope[] }) {
  if (scopes.length === 0) return null;
  const hasGlobal = scopes.some((s) => s.scopeType === 'global');
  const restritos = scopes.filter((s) => s.scopeType !== 'global').length;

  if (hasGlobal && restritos === 0) {
    return <Badge variant="success" size="sm" data-testid="visibility-badge-global">Global</Badge>;
  }
  if (!hasGlobal && restritos > 0) {
    return (
      <Badge variant="warning" size="sm" data-testid="visibility-badge-restrito">
        Restrito: {restritos}
      </Badge>
    );
  }
  return (
    <Badge variant="info" size="sm" data-testid="visibility-badge-misto">
      Global + {restritos}
    </Badge>
  );
}
